import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Bell, Mail, Save, CheckCircle2 } from 'lucide-react';
import AdminWelcomeSettings from './AdminWelcomeSettings';
import EmailAutomationSection from './EmailAutomationSection';
import AdminBrandingSection from './AdminBrandingSection';
import { fmtDateTimeIST } from '../lib/tz';


const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminSettings() {
  const { getHeaders } = useAuth();
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/alert-settings`, { headers: getHeaders() });
      setPrimaryEmail(res.data.primary_email || '');
      setSecondaryEmail(res.data.secondary_email || '');
      if (res.data.updated_at) setLastSaved(res.data.updated_at);
    } catch { /* first load, no settings yet */ }
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!primaryEmail.trim()) {
      toast.error('Primary email is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/alert-settings`, {
        primary_email: primaryEmail.trim(),
        secondary_email: secondaryEmail.trim()
      }, { headers: getHeaders() });
      toast.success('Alert settings saved');
      setLastSaved(new Date().toISOString());
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  return (
    <div className="w-full" data-testid="admin-settings-page">
      <div className="mb-6">
        <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-[#1A2421]">Settings</h1>
        <p className="text-sm text-[#5C6B64] mt-1">Configure alerts and notifications</p>
      </div>

      <Card className="border-[#E2E8DF] shadow-sm" data-testid="alert-email-card">
        <CardHeader className="py-3 px-4 border-b border-[#E2E8DF] bg-[#F9F9F8]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <Bell className="w-4 h-4 text-[#C45A41]" />
              Alert Email Settings
            </CardTitle>
            {primaryEmail && (
              <Badge className="bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20 text-[10px] gap-0.5 h-5">
                <CheckCircle2 className="w-2.5 h-2.5" /> Active
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-4 py-4">
          <p className="text-sm text-[#5C6B64] mb-4">
            These emails receive checklist failure alerts when any item is marked NO during submission. Applies globally to all outlets.
          </p>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64] flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Primary Email <span className="text-[#C45A41]">*</span>
              </Label>
              <Input type="email" placeholder="admin@company.com"
                value={primaryEmail}
                onChange={e => setPrimaryEmail(e.target.value)}
                className="h-10 border-[#E2E8DF] bg-white"
                data-testid="primary-email-input" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64] flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Secondary Email <span className="text-xs text-[#9CA3AF]">(optional)</span>
              </Label>
              <Input type="email" placeholder="backup@company.com"
                value={secondaryEmail}
                onChange={e => setSecondaryEmail(e.target.value)}
                className="h-10 border-[#E2E8DF] bg-white"
                data-testid="secondary-email-input" />
            </div>

            <div className="pt-2 border-t border-[#E2E8DF] flex items-center justify-between">
              <div>
                {lastSaved && (
                  <p className="text-[10px] text-[#5C6B64]">
                    Last saved: {fmtDateTimeIST(lastSaved)}
                  </p>
                )}
              </div>
              <Button onClick={save} disabled={saving}
                className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white h-10 gap-1.5"
                data-testid="save-alert-settings-btn">
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>

          <div className="mt-4 p-3 bg-[#F9F9F8] rounded-lg border border-[#E2E8DF]">
            <p className="text-xs font-medium text-[#5C6B64] uppercase tracking-wide mb-2">How it works</p>
            <ul className="text-xs text-[#5C6B64] space-y-1.5">
              <li className="flex items-start gap-1.5">
                <span className="text-[#C45A41] mt-0.5">1.</span>
                Manager submits a checklist department with any item marked NO
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-[#C45A41] mt-0.5">2.</span>
                System sends one email per department submission listing all NO items
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-[#C45A41] mt-0.5">3.</span>
                Email includes outlet name, department, items, submitter, and time
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-[#C45A41] mt-0.5">4.</span>
                No email is sent if all items are marked YES
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <EmailAutomationSection />

      <AdminBrandingSection />

      <AdminWelcomeSettings />
    </div>
  );
}
